import React, { useContext, useState, useRef, useEffect, Fragment } from 'react';
import MetsIcon from 'commons/components/MetsIcon';
import { useTranslation } from 'react-i18next';
import ConfigurationContext from 'UserRegistration/contexts/ConfigurationContext';
import DataContext from 'UserRegistration/contexts/DataContext';
import classnames from 'classnames';
import { calculateTotal, isPromoCodeApplicable } from 'UserRegistration/resources/utils/packageSelectionUtils';
import { formatPrice } from '../../../commons/resources/utils/numeric';
import './styles.scss';
import moment from 'moment';
import 'moment/locale/fr';
import { useFetchPromoCode } from './hooks/useFetchPromoCode';
import { NotFoundError } from 'commons/errors/notFound';
import { updateBillingRequired } from 'UserRegistration/contexts/configs/componentConfig';


/**
 * Component that renders the order details in the sidebar of the UserRegistration.
 */
function SidebarOrderDetails() {

    const { componentConfig, setComponentConfig } = useContext(ConfigurationContext);
    const { registrationData, setRegistrationData } = useContext(DataContext);
    /* A ref is created pointing to registrationData because the variable is updated from within useEffect. 
     * Because it's a context state variable, without a ref, there is a risk of a stale version of the variable being accessed 
     * and subsequently used to update an already more recent version of the state. This is particularly dangerous because 
     * many different concurrently running components update the registrationData object.
     */
    const registrationDataRef = useRef(registrationData);
    const { t, i18n } = useTranslation(componentConfig.i18nNamespaces);
    const maxItemsToDisplay = 5;
    const [itemsToShow, setItemsToShow] = useState(maxItemsToDisplay);
    const [expandLabel, setExpandLabel] = useState(t('show.more'));
    const [isExpanded, setIsExpanded] = useState(false);
    const [showExpand, setShowExpand] = useState(false);
    const frequency = registrationData.registrationModel.subscription.selectedFrequency;
    const datePattern = registrationData.registrationModel.datePattern;
    const promotionType = registrationData.registrationModel.promotion.type;
    const freeMonthsAmount = registrationData.registrationModel.promotion.amount;
    const [firstLoad, setFirstLoad] = useState(false);
    const frequencyLabelKey = (() => {

        if (frequency === 'YEARLY') {
            return 'year';
        } else if (frequency === 'BI_YEARLY') {
            return 'bi_yearly';
        } else if (frequency === 'MONTHLY') {
            return 'month';
        } else {
            return '';
        }

    })();
    const frequencyText = t('frequency.' + registrationData.registrationModel.subscription.selectedFrequency + '.name');
    // Private Construction cost, only used when PC is enabled.
    let pcCost = 0;

    // Calculate Private Construction Cost.
    if (componentConfig.isPCEnabled && registrationData.registrationModel.selectedSecondarySubscriptions.length) {
        registrationData.registrationModel.selectedSecondarySubscriptions.forEach((item) => {
            pcCost += item.totalPrices[registrationData.registrationModel.subscription.selectedFrequency];
        });
    }

    //set the user's preferred language
    moment.locale(i18n.language);

    const todayPlusOneYear = () => {
        return moment().add(1, 'years').format(datePattern);
    }

    const todayPlusTwoYears = () => {
        return moment().add(2, 'years').format(datePattern);
    }

    const todayPlusOneMonth = () => {
        return moment().add(1, 'months').format(datePattern);
    }

    const todayPlusFreeMonths = () => {
        return moment().add(freeMonthsAmount, 'months').format(datePattern);
    }

    const nextBillingDate = () => {
        if (isPromoCodeApplicable(registrationData) && promotionType == 'FREE_MONTHS') {
            return todayPlusFreeMonths();
        } else if (frequency === 'YEARLY') {
            return todayPlusOneYear();
        } else if (frequency == 'BI_YEARLY') {
            return todayPlusTwoYears();
        } else if (frequency === 'MONTHLY') {
            return todayPlusOneMonth();
        }
    }

    useEffect(() => {
        registrationDataRef.current = registrationData;
    });

    useEffect(() => {

        const itemsCount = registrationData.invoiceItemIDs.length;
        
        if (itemsCount === 0) {
            // reset to default state
            setItemsToShow(maxItemsToDisplay);
            setIsExpanded(false);  
            setExpandLabel(t('show.more')); 
        }

        setShowExpand(itemsCount > maxItemsToDisplay);

        calculateTotal(registrationDataRef.current, setRegistrationData);
        
    }, [registrationData.registrationModel.subscription.selectedFrequency,
        registrationData.registrationModel.subscription.selectedGroup.groupId,
        registrationData.registrationModel.promotion.amount,
        registrationData.registrationModel.promoDiscountAmount,
        JSON.stringify(registrationData.registrationModel.selectedSecondarySubscriptions),
        JSON.stringify(registrationData.registrationModel.subscription.vasRemovedIds),
        JSON.stringify(registrationData.registrationModel.subscription.selectedGroup.selectedSubscriptionItemIds)]);

    useEffect(() => {
        if (registrationData.priceTaxesInfo.subTotal > 0) { 
                setFirstLoad(true);
        }
    }, [registrationData.priceTaxesInfo.subTotal]);
       
    
    function renderShowMoreLink() {

        return (
            <div>
                <a href="#" 
                    className={classnames('showMoreLessLink', showExpand ? 'show' : 'hide')} 
                    onClick={(e) => {

                        e.preventDefault();
                        e.stopPropagation();

                        let itemsCount = registrationData.invoiceItemIDs.length;

                        if (itemsCount > 0) {
                            if (isExpanded) {
                                // executing "Show Less"
                                setItemsToShow(itemsCount > maxItemsToDisplay ? maxItemsToDisplay : itemsCount);
                                setIsExpanded(false);  
                                setExpandLabel(t('show.more'));               
                            } else {
                                // executing "Show more"
                                setItemsToShow(itemsCount);
                                setIsExpanded(true);
                                setExpandLabel(t('show.less'));
                            }
                        } else {
                            // reset to default state
                            setItemsToShow(maxItemsToDisplay);
                            setIsExpanded(false);
                            setExpandLabel(t('show.more'));
                        }

                }}>{expandLabel}</a>
            </div>
        );
    }

    function handleRemoveVASItem(vasItemId) {
        const updatedSubscription = {...registrationData.registrationModel.subscription};
        
        // Remove the VAS item with the specified ID from the subscription
        updatedSubscription.vasItemIds = updatedSubscription.vasItemIds.filter(id => id !== vasItemId);
        
        // Update the registration data with the new subscription data
        const updatedRegistrationData = {...registrationData, registrationModel: {...registrationData.registrationModel, subscription: updatedSubscription}};
        
        // Update the billing components and metadata
        updateBillingRequired(updatedRegistrationData, componentConfig, setComponentConfig);

        if (!updatedSubscription.vasRemovedIds) {
            updatedSubscription.vasRemovedIds = [];
          }

        //add the removed vas id to the list of removed vas ids
        updatedSubscription.vasRemovedIds.push(vasItemId);

        // Update the registration data state
        setRegistrationData(updatedRegistrationData);
    }

    function renderPackagePrice(registrationData) {
        if (componentConfig.componentDefinition.PublicPackageSelector.props.showSynchronizedSubscriptionItemSelector) {
            return;
        }

        if (!registrationData.registrationModel.selectedSubscriptionPackage) {
            return (
                <div className="value" data-value={0}>
                    {formatPrice(
                        0,
                        i18n.language, 
                        componentConfig.currencyCode)}
                </div> 
            );
        }

        let packagePrice = 0;
        if (!registrationData.registrationModel.selectedSubscriptionPackage.freePackage) {
            packagePrice = registrationData.registrationModel.selectedSubscriptionPackage.subscriptionItems[0].totalPrices[registrationData.registrationModel.subscription.selectedFrequency];
        }

        return (
            <div className="value" data-value={packagePrice}>
                {formatPrice(
                    packagePrice,
                    i18n.language, 
                    componentConfig.currencyCode)}
            </div> 
        );
    }
    

    function renderDiscountTitle(registrationData) {
        if (registrationData.priceTaxesInfo.discountAmount || isPromoCodeApplicable(registrationData)) {
            return (
                <div className="section no-top-border">
                    <div className="order-row total">
                        <div className="name">{t("discount")}</div>
                    </div>
                </div>
            )
        }
    }

    /**
     * Renders the discount percentage information.
     * Contains some temporary code revolving around the banner discount until August 1st 2023.
     * @param registrationData
     * @returns {JSX.Element|string}
     */
    function renderDiscountPercentage(registrationData) {
        if (!registrationData.priceTaxesInfo.discountAmount) {
            return '';
        }
        let discountPercentageFragment = t('discountPercentage', {
            discountPercentage: registrationData.priceTaxesInfo.discountPercentage,
            // METS-1469 : in case of a discount, we want to display "1 state" instead of "0 state"
            stateCount: registrationData.registrationModel.subscription.selectedGroup.selectedSubscriptionItemIds.length == 0 ? '1' : registrationData.registrationModel.subscription.selectedGroup.selectedSubscriptionItemIds.length < 4 ? registrationData.registrationModel.subscription.selectedGroup.selectedSubscriptionItemIds.length : '3+'
        });

        return (
            <div>
                <div className="order-row">
                    <div className="name">{discountPercentageFragment}</div>
                    <div className="discountValue" data-value={registrationData.priceTaxesInfo.discountAmount}>-{formatPrice(registrationData.priceTaxesInfo.discountAmount, i18n.language, componentConfig.currencyCode)}</div>
                </div>
            </div>
        )
    }

    function renderSubTotalRow(registrationData) {
        let subTotal = registrationData.priceTaxesInfo.subTotal;
        return (
            <div className="order-row total">
                <div className="name">{t("sub-total")}</div>
                <div className="value"
                     data-value={registrationData.priceTaxesInfo.total}>{formatPrice(subTotal, i18n.language, componentConfig.currencyCode)}</div>
            </div>
        )
    }

    function renderPromotionType(registrationData) {
        switch (registrationData.registrationModel.promotion.type) {
            case 'FREE_MONTHS':
                return (
                    <Fragment>
                        <div className="name">{t("paymentDueNow")}</div>
                        <div className="value" data-value={0}>{formatPrice(0, i18n.language, componentConfig.currencyCode)}</div>
                    </Fragment>
                )
            case 'PERCENTAGE':
                break;
            default:
                return '';
        }
    }

    function renderPromotionCode(registrationData) {
        if (registrationData.registrationModel.promotion.code) {
            return (
                <div className="promotion-code-container">
                    <MetsIcon name="svg_tag" className="icon" />
                    <div className="code">
                        {registrationData.registrationModel.promotion.code.toUpperCase()}
                    </div>
                    <div className="promo-applied">
                        <span className="promo-applied-value">{t('promoCodeApplied')}</span>
                    </div>
                    <div className="clear"></div>
                </div>
            )
        }

    }

    function renderDiscountCode(registrationData) {
        const [value, setValue] = useState("");
        const [visible, setVisible] = useState(true);
        const { refetch, isError, error} = useFetchPromoCode(value);
    
        const { componentConfig } = useContext(ConfigurationContext);
        const { t } = useTranslation(componentConfig.i18nNamespaces);
        const promoCodeFromUrl = registrationData.registrationModel.promotion.id;
        const noPromoCodeFromUrl = registrationData.registrationModel.promotion.type;
        let isPromoCodeOutDated = false;
        let freePackageSelected = false;

        const removeElement = () => {
            setVisible((prev) => !prev);
        }
    
        const buttonStyle = {
            backgroundColor: '#064D71'
        };
    
        function handleChange(e) {
            setValue(e.target.value);
        }
    
        function handleClick() {
            refetch();
        }

        if (registrationData.registrationModel.promotion.supportedSubscriptionItems) {
            const obj = registrationData.registrationModel.promotion.supportedSubscriptionItems;
            isPromoCodeOutDated = Object.keys(obj).length === 0;
        }
        
        if (registrationData.registrationModel.selectedSubscriptionPackage) {
            freePackageSelected = registrationData.registrationModel.selectedSubscriptionPackage.freePackage 
                && !registrationData.registrationModel.selectedSecondarySubscriptions.length;
        }

        if (!firstLoad || freePackageSelected || promoCodeFromUrl && !isPromoCodeOutDated || componentConfig.hidePromoCodeInput) {
            return <></>
        } else if (isError && error instanceof NotFoundError || noPromoCodeFromUrl == "Invalid promo code" || isPromoCodeOutDated ) {
            return <div className='promo-code'>
                <div>
                <input className='promo-code-input' type="text" name='pro' placeholder={registrationData.registrationModel.promotion.code ? registrationData.registrationModel.promotion.code : t('enter.promo.code')} value={value} onChange={handleChange}/>
                <button type="button" disabled={!value} className='button mets-command-button defaultBtn' style={buttonStyle} onClick={handleClick}>{t('apply')}</button>
                </div>
                <p className='error-message'>{t('code.does.not.exist')}</p>
            </div>
            
        } else {
            return <div className='promo-code'>
            {
                visible 
                ?
                <a onClick={removeElement}>{t("add.a.promo.code")}</a>
                : <div>
                <input className='promo-code-input' type="text" name='pro' placeholder={t('enter.promo.code')} value={value} onChange={handleChange}/>
                <button type="button" disabled={!value} className='button mets-command-button' style={buttonStyle} onClick={handleClick}>{t('apply')}</button>
                </div>
            }
            </div>
    
        }
        
    }

    function renderPromotionDescription(registrationData) {
        switch (registrationData.registrationModel.promotion.type) {
            case 'FREE_MONTHS':
                return (
                    <div className="order-row">
                        <div className="name">{registrationData.registrationModel.promotion.description[i18n.language.toUpperCase()]}</div>
                        <div className="discountValue" data-value={0}>-{formatPrice(0, i18n.language, componentConfig.currencyCode)}</div>
                    </div>
                )
            case 'PERCENTAGE':
                return (
                    <div className="order-row">
                        <div className="name">{registrationData.registrationModel.promotion.description[i18n.language.toUpperCase()]}</div>
                        <div className="discountValue" data-value={0}>-{formatPrice(registrationData.priceTaxesInfo.promoDiscountAmount, i18n.language, componentConfig.currencyCode)}</div>
                    </div>
                )
            default:
                return '';
        }
    }

    return (
        
        <div className="order-details" data-testid="sidebar-order-details" id="sidebar-order-details">
            <div className="order-details-inner">

                <h2>{t("orderDetails")}</h2>

                <div className="section">
                    <div className="order-row sub-total">
                        <div className="name">
                            {registrationData.registrationModel.subscription.selectedGroup.cmsKey && t(registrationData.registrationModel.subscription.selectedGroup.cmsKey + '.optionName')}
                            {!registrationData.registrationModel.subscription.selectedGroup.cmsKey && t('supplier.services.group.notSelected')}
                        </div>

                        {renderPackagePrice(registrationData)}

                    </div>
                    {registrationData.metadata.isBillingRequired && registrationData.registrationModel.subscription.selectedGroup.cmsKey &&
                        <div className="order-row frequency">
                            {frequencyText && 
                                <div className="name">({frequencyText})</div>
                            }
                        </div>
                    }

                    {!componentConfig.componentDefinition.PublicPackageSelector.props.showSynchronizedSubscriptionItemSelector ?
                        <div className="order-row ordered-item">
                            <div className="name">{registrationData.priceTaxesInfo.orderedPackage}</div>
                        </div>
                    : ''}
                </div>

                {componentConfig.isPCEnabled && registrationData.registrationModel.selectedSecondarySubscriptions.length > 0 &&
                    <div className="section">
                        <div className="order-row sub-total">
                            <div className="name">{t('pc.sidebarTitle')}</div>
                            <div className="value" data-value={formatPrice(pcCost, i18n.language, componentConfig.currencyCode)}>{formatPrice(pcCost, i18n.language, componentConfig.currencyCode)}</div>
                        </div>
                        
                        {registrationData.registrationModel.selectedSecondarySubscriptions.sort((a, b) => (a['description'] < b['description']) ? -1 : (a['description'] > b['description']) ? 1 : 0).map((subscription, index) => {
                            return (
                                <div className="order-row" key={index}>
                                    <div className="name">{subscription.description}</div>
                                </div>
                            );
                        })}
                    </div>
                }
                
                { (componentConfig.componentDefinition.PublicPackageSelector.props.showSynchronizedSubscriptionItemSelector && registrationData.registrationModel.selectedSubscriptionPackage && registrationData.invoiceItemIDs.length > 0 )
                 || registrationData.registrationModel.subscription.vasItemIds ?
                    <div className="section">
                        {registrationData.invoiceItemIDs.slice(0, itemsToShow).map((subscriptionItemId) => {
                            return registrationData.registrationModel.selectedSubscriptionPackage.subscriptionItems.map((subscriptionItem) => {
                                if (subscriptionItem.id == subscriptionItemId) {
                                    return (
                                        <div className="order-row sub-total">
                                            <div className="name">{subscriptionItem.description}<br /><span className="purchasingGroup">{subscriptionItem.purchasingGroupName}</span></div>
                                        </div>
                                    )
                                }
                            })
                        })}

                    {renderShowMoreLink()}

                    {registrationData.registrationModel.subscription.vasItemIds ?
                    <div>
                        {registrationData.registrationModel.subscription.vasItemIds.map((vasItemId) => {
                            return componentConfig.componentDefinition.PublicPersonalInformation.props.availableVasItems.map((vasItem) => {
                                if (vasItemId === vasItem.id) {
                                    const vasRemovedIds = registrationData.registrationModel.subscription.vasRemovedIds || [];
                                    const isRemoved = vasRemovedIds.includes(vasItem.id);
                                    if (!isRemoved) {
                                        return (
                                        <div className="order-row sub-total">
                                            <div className="name">{t('supplier.services.vas.' + vasItem.itemCode)}</div>
                                            <div className="value">{formatPrice(vasItem.totalPrices[registrationData.registrationModel.subscription.selectedFrequency], i18n.language, componentConfig.currencyCode)}</div>
                                            <div className="remove-link">
                                                <a onClick={() => handleRemoveVASItem(vasItem.id)}>{t('remove')}</a>
                                            </div>
                                        </div>

                                        );
                                    }
                                  }
                                  
                            })
                        })}
                    </div> 
                    : ''}
                </div>
                : ''}
                
                <div className="section">
                    {renderSubTotalRow(registrationData)}
                    {renderDiscountTitle(registrationData)}
                    {renderDiscountPercentage(registrationData)}

                    {isPromoCodeApplicable(registrationData) ?
                        renderPromotionDescription(registrationData)
                        : ''
                    }
                    {isPromoCodeApplicable(registrationData) ?
                        renderPromotionCode(registrationData)
                        : ''
                    }

                    {renderDiscountCode(registrationData)}

                </div>

                {registrationData.priceTaxesInfo.taxes.length ?
                    <div className="section line-items">
                        {registrationData.priceTaxesInfo.taxes.map((tax, index) => {
                            return (
                                <div className="order-row tax" key={index}>
                                    <div className="name">

                                    {t('taxes.' + tax.name) !== 'taxes.' + tax.name ? (
                                        `${t('taxes.' + tax.name)} (${tax.rate}%)`
                                        ) :  t('taxes.generic')
                                    }

                                    </div>
                                    <div className="value" data-value={tax.amount}>{formatPrice(tax.amount, i18n.language, componentConfig.currencyCode)}</div>
                                </div>
                            );
                        })}
                    </div>
                    : ''}

                <div className="section">
                    <div className="order-row total">
                        <div className="name">{t("total")}</div>
                        <div className="value" data-value={registrationData.priceTaxesInfo.total}>{formatPrice(registrationData.priceTaxesInfo.total, i18n.language, componentConfig.currencyCode)}</div>
                    </div>
                    <div className="order-row total">
                        {isPromoCodeApplicable(registrationData) ?
                            renderPromotionType(registrationData)
                            : ''
                        }
                    </div>
                </div>

                {registrationData.metadata.isBillingRequired && 
                    frequencyLabelKey &&
                    <p className="billing-note">
                        {t('orderDetails.explanation', {
                            subTotal: formatPrice(registrationData.priceTaxesInfo.recurringSubTotal, i18n.language, componentConfig.currencyCode),
                            frequency: frequency === null || frequency === undefined ? '' : t(frequency.toLowerCase()),
                            billingInterval: frequencyLabelKey ? t(frequencyLabelKey) : '',
                            nextBillingInterval: frequencyLabelKey ? t('next.' + frequencyLabelKey) : '',
                            everyBillingInterval: frequencyLabelKey ? t('every.' + frequencyLabelKey) : '',
                            nextBillingDate: nextBillingDate(),
                        })}
                    </p>
                }

            </div>

        </div>
    );

}



export default SidebarOrderDetails;
